import type { Configuration, AccountInfo } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';
import type { AppRole } from '../types/auth';

/**
 * MSAL configuration for Entra ID authentication.
 *
 * Client ID and Tenant ID are read from environment variables.
 * See .env.example for required variables.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID || 'ca3dfc8e-f323-409e-b6d6-211385417ef8'}`,
    // MSAL v5 redirect bridge (QMS ISS-295, 29 Oct 2026): sign-ins return to a tiny page that hands the response to
    // the waiting window, never to the app itself. Each host's `/redirect.html` is registered on the Entra app (SPA).
    redirectUri: `${window.location.origin}/redirect.html`,
  },
  cache: {
    cacheLocation: 'localStorage', // SSO across tabs
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
        }
      },
    },
  },
};

/** Scopes requested during login — includes Graph API scopes so tokens
 *  are acquired upfront (avoids silent iframe renewal which times out
 *  in Edge due to cross-origin / third-party cookie restrictions) */
export const loginRequest = {
  scopes: ['User.Read', 'Sites.ReadWrite.All'],
};

/** Scopes for Microsoft Graph API access (SharePoint) — used by internal staff,
 *  who read/write SharePoint directly via delegated Graph. */
export const graphScopes = {
  scopes: ['User.Read', 'Sites.ReadWrite.All'],
};

/**
 * The backend API's exposed scope (`api://<api-app-id>/access_as_user`).
 *
 * AP (B2B guest) flows call our own same-origin `/api` instead of Graph —
 * SharePoint refuses guests' delegated Graph tokens, so the API does the work
 * app-only and enforces per-AP isolation. The SPA is pre-authorized on this
 * scope, so acquiring it needs no consent prompt.
 */
const apiAppId = import.meta.env.VITE_API_APP_ID || '9ded6655-7c6d-4d9d-b640-946b13bad7f5';
export const apiScopes = {
  scopes: [`api://${apiAppId}/access_as_user`],
};

/**
 * Extract app roles from an MSAL account's ID token claims.
 * Roles are assigned in Entra ID Enterprise Applications.
 */
export function getRolesFromAccount(account: AccountInfo | null): AppRole[] {
  if (!account) return [];
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const roles = claims?.roles;
  if (Array.isArray(roles)) {
    return roles as AppRole[];
  }
  return [];
}

/**
 * Convert a B2B guest UPN like `john_gmail.com#ext#@tenant.onmicrosoft.com`
 * back to the plain email `john@gmail.com`. Returns null if it isn't an `#ext#`
 * form. (Entra mangles the guest's email by replacing its `@` with `_`.)
 */
export function demangleGuestUpn(s: string): string | null {
  const m = s.match(/^(.+)#ext#@/i);
  if (!m) return null;
  const left = m[1];
  const i = left.lastIndexOf('_');
  if (i <= 0) return null;
  return (left.slice(0, i) + '@' + left.slice(i + 1)).toLowerCase();
}

/**
 * Every identity string a login could be matched against (lowercased, deduped).
 *
 * Entra returns different forms for different B2B guest types: email one-time-
 * passcode / federated gmail guests present the **plain email**, while others
 * present the **`#ext#` UPN**. We collect `account.username` plus the
 * `preferred_username` / `email` / `upn` claims, and for any `#ext#` form we also
 * add its de-mangled plain email. Matching an AP against this whole set means the
 * exact stored form (in `AP_Master.Email`) no longer has to be guessed.
 */
export function getLoginIdentities(account: AccountInfo | null): string[] {
  if (!account) return [];
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const raw: Array<unknown> = [
    account.username,
    claims?.preferred_username,
    claims?.email,
    claims?.upn,
  ];
  const set = new Set<string>();
  for (const v of raw) {
    if (typeof v !== 'string' || !v) continue;
    set.add(v.toLowerCase());
    const plain = demangleGuestUpn(v);
    if (plain) set.add(plain);
  }
  return [...set];
}
